<template>
  <a-list :data-source="cloudaccounts" :key="updated" class="cloudaccount-setup-list">
    <a-list-item slot="renderItem" slot-scope="cloudaccount" :key="cloudaccount.id" class="cloudaccount-setup-list-item" :class="{ active: cloudaccount.id === activeItem }">
      <a-row type="flex" justify="space-around" align="middle" style="width: 207px;">
        <a-col :span="18">
          <div style="margin-left: 5px;">
            <a @click="showSummaryView(cloudaccount)">{{ cloudaccount.name || '-' }}</a>
            <p>{{ cloudaccount.provider }}</p>
          </div>
        </a-col>
        <a-col :span="6">
          <a-tooltip>
            <template slot="title">
              {{ cloudaccount.setup.desc }}
            </template>
            <a-icon @click="showSummaryView(cloudaccount)" :type="cloudaccount.setup.icon" :style="{ fontSize: '32px', color: cloudaccount.setup.color }" :spin="cloudaccount.setup.spin" />
          </a-tooltip>
        </a-col>
      </a-row>
    </a-list-item>
    <div v-if="list.loading" class="cloudaccount-setup-list-loading">
      <a-spin />
    </div>
  </a-list>
</template>

<script>
import _ from 'lodash'
import * as R from 'ramda'
import { mapGetters, mapState } from 'vuex'
import { CLOUDACCOUNT_SETUP_STATUS } from '../../consts'
import { getRequestT } from '@/utils/utils'

export default {
  name: 'CloudAccountSetupList',
  props: {
    search: {
      type: String,
      required: false,
      default: '',
    },
  },
  data () {
    return {
      updated: 0,
      activeItem: '',
      list: this.$list.createList(this, {
        resource: 'cloudaccounts',
        apiVersion: 'v2',
        getParams: this.getParams,
      }),
    }
  },
  computed: {
    ...mapGetters(['isAdminMode', 'scope']),
    ...mapState('app', {
      user_guide: state => state.user_guide,
    }),
    cloudaccounts () {
      if (this.list.data) {
        return Object.values(this.list.data).map(item => {
          return {
            id: item.data.id,
            name: item.data.name,
            status: item.data.status,
            enabled: item.data.enabled,
            provider: item.data.provider,
            brand: item.data.brand,
            balancer: item.data.balancer,
            last_sync: item.data.last_sync,
            last_sync_end_at: item.data.last_sync_end_at,
            proxy_setting: item.data.proxy_setting,
            setup: { status: 'checking', ...CLOUDACCOUNT_SETUP_STATUS.checking },
            regions: [],
          }
        })
      } else {
        return []
      }
    },
  },
  watch: {
    cloudaccounts (items) {
      this.fullFillAccounts(items)
    },
    search () {
      _.debounce(this.list.fetchData(0, 20), 500)
    },
  },
  created () {
    // todo: 当前最多支持20个账号, 后续支持动态加载？
    this.list.fetchData(0, 20)
  },
  methods: {
    async checkingNetwork (cloudaccount) {
      try {
        // 如果vpc数量大于0 并且主机数量大于0，粗略认为已经存在可用子网。以减少网络请求熟练
        if (cloudaccount.vpc_count && cloudaccount.vpc_count > 0 && cloudaccount.guest_count && cloudaccount.guest_count > 0) {
          return 'configured'
        }

        const res = await new this.$Manager('networks').list({
          params: { cloudaccount_id: cloudaccount.id, limit: 1, status: 'available', scope: this.scope, $t: getRequestT() },
        })
        if (res.data && res.data.total && res.data.total >= 1) {
          return 'configured'
        } else {
          return 'pending'
        }
      } catch (e) {
        throw e
      }
    },
    async getCloudaccountSetupStatus (cloudaccount) {
      // 同步中
      if (['syncing', 'queued', 'queuing', 'error'].indexOf(cloudaccount.sync_status) >= 0) {
        return cloudaccount.sync_status
      }

      // 同步完成
      if (['init', 'disconnected', 'deleting', 'deleted', 'delete_failed', 'sync_network'].indexOf(cloudaccount.status) >= 0) {
        return cloudaccount.status
      }

      // 健康状态
      if (['insufficient', 'suspended', 'arrears', 'unknown'].indexOf(cloudaccount.health_status) >= 0) {
        return cloudaccount.health_status
      }

      // 启用禁用
      if (!cloudaccount.enabled) {
        return 'disabled'
      }

      // 查询网络配置
      return await this.checkingNetwork(cloudaccount)
    },
    async fullFillAccounts (cloudaccounts) {
      for (const item of cloudaccounts) {
        let status = 'check_failed'
        try {
          status = await this.getCloudaccountSetupStatus(item)
        } catch (e) {
          console.log(e)
        }

        if (CLOUDACCOUNT_SETUP_STATUS.hasOwnProperty(status)) { item.setup = { status: status, ...CLOUDACCOUNT_SETUP_STATUS[status] } }
        this.$nextTick(() => {
          this.updated += 1
        })
      }

      if (!this.cloudaccounts || this.cloudaccounts.length === 0) {
        this.changeView('CloudAccountSetupEmptyPage', {})
      } else {
        if (this.user_guide.first_time_setup) {
          if (R.any(a => { return a.setup && a.setup.status === 'configured' })(this.cloudaccounts)) {
            this.$store.dispatch('app/finishFirstTimeUserGuide')
          }
        }
        this.cloudaccounts.map(a => { return a.setup && a.setup.status && a.setup.status === 'user_guide' })
        this.showSummaryView(this.cloudaccounts[0])
      }
    },
    showSummaryView (cloudaccount) {
      this.activeItem = cloudaccount.id || ''
      this.changeView('CloudAccountSetupSummary', cloudaccount)
    },
    showSetupView (cloudaccount) {
      this.activeItem = cloudaccount.id || ''
      this.changeView('CloudAccountSetup', cloudaccount)
    },
    changeView (viewName, viewData) {
      this.$emit('changeView', { name: viewName, data: viewData || {} })
    },
    getParams () {
      const params = { public_cloud: true }
      if (this.search) {
        params['filter.0'] = `name.contains(${this.search})`
      }
      return params
    },
  },
}
</script>

<style lang="less" scoped>
@import "~@/styles/less/theme";

.cloudaccount-setup-list {
  border-right: 1px solid #e8e8e8;
  overflow: auto;

  .cloudaccount-setup-list-item {
    padding-left: 8px;

    &:hover {
      background-color: rgb(230, 247, 255);
    }

    &.active {
      border-left: 2px solid @primary-color;
      background-color: rgb(230, 247, 255);
    }
  }
}

.cloudaccount-setup-list-loading {
  position: absolute;
  bottom: 40px;
  width: 100%;
  text-align: center;
}
</style>
